import { useTranslation } from 'react-i18next';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import MicNoneOutlined from '@mui/icons-material/MicNoneOutlined';
import Divider from 'shared/components/divider/divider';
import SonevoSlider from 'shared/components/slider/sonevo-slider';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AcousticTimeFrameConfiguration from 'features/residents/models/configurations/acoustic-time-frame-configuration';
import { useAcousticSettings } from './hooks';
import styles from './acoustic-settings.module.scss';

type Props = {
  acousticConfiguration: AcousticConfiguration;
  activeAllDay: boolean;
  index: number;
  valuesChanged: (newTimeFrameConfigurations: AcousticTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  updateResidentSettingEditFormScenario: () => void;
  isTemporaryConfiguration: boolean;
  tempConfigSoundThresholdChanged?: (value: number | number[]) => void;
  tempConfigDurationChanged?: (value: number | number[]) => void;
};

const AcousticSettings = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const { viewingMode, soundThresholdChanged, durationChanged } = useAcousticSettings(props);

  const sliderMarks = [
    { value: 1, label: '1' },
    { value: 2 },
    { value: 3 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
    { value: 6 },
    { value: 7 },
    { value: 8 },
    { value: 9, label: '9' },
  ];
  const minValue = 1;
  const maxValue = 9;

  return (
    <div className={styles.acousticSettingsContainer}>
      <SonevoSlider
        label={
          props.isTemporaryConfiguration
            ? t('acousticScenarioConfiguration.temporarySoundThreshold')
            : t('acousticScenarioConfiguration.soundThreshold')
        }
        value={
          props.isTemporaryConfiguration
            ? props.acousticConfiguration.temporaryConfiguration?.soundThreshold
            : (props.acousticConfiguration.acousticTimeFrameConfigurations?.[props.index]?.soundThreshold ?? 4)
        }
        disabled={
          viewingMode === 'viewing' || (!props.isTemporaryConfiguration && props.acousticConfiguration.temporaryConfigurationEnabled)
        }
        onChange={(_, value) => soundThresholdChanged(value as number)}
        onChangeCommitted={props.updateResidentSettingEditFormScenario}
        sliderIconStart={<MicNoneOutlined className={styles.sliderIconSmall} color={viewingMode === 'viewing' ? 'disabled' : 'primary'} />}
        sliderIconEnd={<MicNoneOutlined className={styles.sliderIcon} color={viewingMode === 'viewing' ? 'disabled' : 'primary'} />}
        sliderMarks={sliderMarks}
        minValue={minValue}
        maxValue={maxValue}
      />
      <SonevoSlider
        label={
          props.isTemporaryConfiguration
            ? t('acousticScenarioConfiguration.temporaryDuration')
            : t('acousticScenarioConfiguration.duration')
        }
        value={
          props.isTemporaryConfiguration
            ? props.acousticConfiguration.temporaryConfiguration?.duration
            : (props.acousticConfiguration.acousticTimeFrameConfigurations?.[props.index]?.duration ?? 4)
        }
        disabled={
          viewingMode === 'viewing' || (!props.isTemporaryConfiguration && props.acousticConfiguration.temporaryConfigurationEnabled)
        }
        onChange={(_, value) => durationChanged(value as number)}
        onChangeCommitted={props.updateResidentSettingEditFormScenario}
        sliderIconStart={
          <AccessTimeRounded className={styles.sliderIconSmall} color={viewingMode === 'viewing' ? 'disabled' : 'primary'} />
        }
        sliderIconEnd={<AccessTimeRounded className={styles.sliderIcon} color={viewingMode === 'viewing' ? 'disabled' : 'primary'} />}
        sliderMarks={sliderMarks}
        minValue={minValue}
        maxValue={maxValue}
      />

      {!props.isTemporaryConfiguration && <Divider className={styles.divider} />}
    </div>
  );
};

export default AcousticSettings;
